import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';

import { LanguageProvider } from './contexts/LanguageContext';
import WhatsAppButton from './components/shared/WhatsAppButton';
import AIAssistant from './components/shared/AIAssistant';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Lazy-loaded pages for code splitting (improves initial load time)
const Home               = lazy(() => import('./pages/Home'));
const About              = lazy(() => import('./pages/About'));
const Courses            = lazy(() => import('./pages/Courses'));
const CourseDetail       = lazy(() => import('./pages/CourseDetail'));
const Universities       = lazy(() => import('./pages/Universities'));
const StudyIndia         = lazy(() => import('./pages/StudyIndia'));
const StudyAbroad        = lazy(() => import('./pages/StudyAbroad'));
const CountryGuide       = lazy(() => import('./pages/CountryGuide'));
const MedicalCounseling  = lazy(() => import('./pages/MedicalCounseling'));
const EngineeringCounseling = lazy(() => import('./pages/EngineeringCounseling'));
const ManagementPrograms = lazy(() => import('./pages/ManagementPrograms'));
const NursingPrograms    = lazy(() => import('./pages/NursingPrograms'));
const Scholarships       = lazy(() => import('./pages/Scholarships'));
const SuccessStories     = lazy(() => import('./pages/SuccessStories'));
const Blog               = lazy(() => import('./pages/Blog'));
const BlogPost           = lazy(() => import('./pages/BlogPost'));
const Contact            = lazy(() => import('./pages/Contact'));
const FreeCounseling     = lazy(() => import('./pages/FreeCounseling'));
const AIRecommendation   = lazy(() => import('./pages/AIRecommendation'));
const StatePage          = lazy(() => import('./pages/StatePage'));
const CityPage           = lazy(() => import('./pages/CityPage'));
const CollegeDetailPage  = lazy(() => import('./pages/CollegeDetailPage'));
const NotFound           = lazy(() => import('./pages/NotFound'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
          style={{ background: 'linear-gradient(135deg,#0B1F45,#1a3a7a)' }}>
          <span className="text-white font-bold text-xl">Y</span>
        </div>
        <div className="flex gap-1.5">
          {[0,1,2].map(i => (
            <span key={i} className="w-2 h-2 bg-navy-400 rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.15}s`, background: '#0B1F45' }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppLayout() {
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/"                        element={<Home />} />
          <Route path="/about"                   element={<About />} />
          <Route path="/courses"                 element={<Courses />} />
          <Route path="/courses/:id"             element={<CourseDetail />} />
          <Route path="/courses/:course/:state"            element={<StatePage />} />
          <Route path="/courses/:course/:state/:city"      element={<CityPage />} />
          <Route path="/college/:slug"                     element={<CollegeDetailPage />} />
          <Route path="/universities"            element={<Universities />} />
          <Route path="/study-india"             element={<StudyIndia />} />
          <Route path="/study-abroad"            element={<StudyAbroad />} />
          <Route path="/country/:id"             element={<CountryGuide />} />
          <Route path="/medical-counseling"      element={<MedicalCounseling />} />
          <Route path="/engineering-counseling"  element={<EngineeringCounseling />} />
          <Route path="/management-programs"     element={<ManagementPrograms />} />
          <Route path="/nursing-programs"        element={<NursingPrograms />} />
          <Route path="/scholarships"            element={<Scholarships />} />
          <Route path="/success-stories"         element={<SuccessStories />} />
          <Route path="/blog"                    element={<Blog />} />
          <Route path="/blog/:id"                element={<BlogPost />} />
          <Route path="/contact"                 element={<Contact />} />
          <Route path="/free-counseling"         element={<FreeCounseling />} />
          <Route path="/ai-recommendation"       element={<AIRecommendation />} />
          <Route path="*"                        element={<NotFound />} />
        </Routes>
      </Suspense>
      <Footer />
      <AIAssistant />
      <WhatsAppButton />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <LanguageProvider>
          <AppLayout />
        </LanguageProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
